// Implements: F-SHR-013, F-UIX-007
/**
 * Mockup v3 P8: "Vista de visitante" (the mockup's `openVisitor`), what someone opening the link
 * sees, framed under a notice that says this visit is not counted. Pure presentation.
 *
 * A modal layer over the whole page (`aria-modal`, so the share popover behind it stays open): the
 * focus moves to its close button, and Escape closes it first (`escapeLayers`). The frame is the one
 * `SandboxedFrame` of the application (AD-04): scripts and forms, never `allow-same-origin`, the
 * same grant the public share shell gives a visitor, so the mockup runs and stays interactive yet
 * gets an opaque origin and can read neither the person's session nor this page.
 */

import { useEffect, useRef } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { SandboxedFrame } from "../../../shared/ui/atoms/SandboxedFrame";
import { pushEscapeLayer } from "../../../shared/ui/escapeLayers";
import type { FrameDocumentPath } from "../../../shared/ui/frameDocumentPath";
import styles from "./VisitorPreview.module.css";

export interface VisitorPreviewProps {
  /** The visitor preview's address (`sharePreviewUrl`). */
  readonly src: FrameDocumentPath;
  /** The link as the notice names it ("Enlace sin crear" before it exists). */
  readonly label: string;
  readonly onClose: () => void;
}

export function VisitorPreview({ src, label, onClose }: VisitorPreviewProps): ReactElement {
  const { t } = useTranslation();
  const closeButton = useRef<HTMLButtonElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    closeButton.current?.focus();
    return pushEscapeLayer(() => {
      closeRef.current();
    });
  }, []);

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true" aria-label={t("sharing.visitor.title")}>
      <div className={styles.bar} role="status">
        <Icon name="eye" size="sm" />
        <b>{t("sharing.visitor.title")}</b>
        <span>{t("sharing.visitor.notice", { label })}</span>
        <span className={styles.spacer} />
        <Button variant="secondary" size="sm" buttonRef={closeButton} onClick={onClose}>
          <Icon name="close" size="sm" />
          {t("sharing.visitor.close")}
        </Button>
      </div>
      <SandboxedFrame className={styles.frame} title={`${t("sharing.visitor.title")}: ${label}`} src={src} />
    </div>
  );
}
